import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAddress } from "@thirdweb-dev/react";
import { ethers } from "ethers";
import "../styles/staking-box2.css";
import { useStakingContract } from "../hooks/useStakingContract";

const StakingBox2 = () => {
  const { t } = useTranslation();
  const address = useAddress();
  const [withdrawInProgress, setWithdrawInProgress] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [poolPercentage, setPoolPercentage] = useState("0.00000");
  
  // Estados para el modal de notificación (estilo StakingBox4)
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success"); // success, error
  
  // Usar el hook de staking
  const { stakedBalance, totalStaked, contract } = useStakingContract();

  // Calcular el porcentaje del pool
  useEffect(() => {
    if (!stakedBalance || !totalStaked || !address) {
      setPoolPercentage("0.00000");
      return;
    }
    
    try {
      const userStakedBN = ethers.BigNumber.from(stakedBalance);
      const totalStakedBN = ethers.BigNumber.from(totalStaked);
      
      if (totalStakedBN.isZero()) {
        setPoolPercentage("0.00000");
        return;
      }

      const userStakedFloat = parseFloat(ethers.utils.formatEther(userStakedBN));
      const totalStakedFloat = parseFloat(ethers.utils.formatEther(totalStakedBN));
      
      if (totalStakedFloat > 0) {
        const percentage = (userStakedFloat / totalStakedFloat) * 100;
        setPoolPercentage(percentage.toFixed(5));
      }
    } catch (error) {
      console.error("Error al calcular porcentaje:", error);
      setPoolPercentage("0.00000");
    }
  }, [stakedBalance, totalStaked, address]);

  // Función simplificada para formatear balance
  const formatBalance = (balance) => {
    if (!balance) return "0";
    try {
      return parseFloat(ethers.utils.formatEther(balance)).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      console.error("Error al formatear balance:", error);
      return "0";
    }
  };

  // Mostrar mensaje en modal en lugar de alert
  const showMessage = (message, type = "success") => {
    setModalMessage(message);
    setModalType(type);
    setShowNotificationModal(true);
  };
  
  // Cerrar modal de notificación
  const closeNotificationModal = () => {
    setShowNotificationModal(false);
  };
  
  // FUNCIÓN DIRECTA: Retiro de tokens sin confirmación
  const withdrawTokens = async () => {
    if (!address || !contract || withdrawInProgress) return;
    
    try {
      // Activar indicador de proceso en el botón
      setWithdrawInProgress(true);
      
      // Llamar directamente al método withdraw() del contrato
      const tx = await contract.withdraw();
      
      // Esperar confirmación - sin mensajes adicionales
      await tx.wait();
      
      // Mostrar mensaje de éxito
      showMessage(t('stakingBox2.tokensWithdrawn'));
      
    } catch (error) {
      console.error("Error al retirar tokens:", error);
      
      // Simplificación de mensajes de error para mostrar solo mensajes amigables
      let errorMessage = t('stakingBox2.errorWithdrawing');
      
      // Detectar posibles casos de rechazo de transacción
      if (error.code === 4001 || 
          (error.message && (
            error.message.includes("user rejected") || 
            error.message.includes("User denied") || 
            error.message.includes("user denied") || 
            error.message.includes("rejected") ||
            error.message.includes("cancelled") ||
            error.message.includes("canceled")
          ))) {
        errorMessage = t('stakingBox2.userRejected');
      } else if (error.message && error.message.includes("insufficient funds")) {
        errorMessage = t('stakingBox2.insufficientFunds');
      } else if (error.message && error.message.includes("execution reverted")) {
        errorMessage = t('stakingBox2.contractRejection');
      }
      
      showMessage(errorMessage, "error");
      
    } finally {
      // Desactivar indicador de proceso
      setWithdrawInProgress(false);
    }
  };
  
  // Verificar si hay tokens para retirar
  const canWithdraw = stakedBalance && 
                    !ethers.BigNumber.from(stakedBalance || "0").isZero() &&
                    address;

  return (
    <div className="staking-box">
      <div className="box-content">
        <div>
          <div className="header-container">
            <h4>{t('stakingBox2.poolPercentage')}</h4>
            <img 
              src="/icons/info-icon-white.svg"
              alt="Información" 
              className="info-icon"
              onClick={() => setShowInfoModal(true)}
            />
          </div>
          <p className="value">{poolPercentage}%</p>
        </div>
        
        <div>
          <h4>{t('stakingBox2.stakedBalance')}</h4>
          <p className="value">
            {address ? formatBalance(stakedBalance) : "0"}
            <span className="unit">$eDOP</span>
          </p>
        </div>
      </div>
      
      {/* BOTÓN DIRECTO: Sin abrir ningún modal */}
      <button 
        id="withdraw-button"
        onClick={withdrawTokens}
        disabled={!canWithdraw || withdrawInProgress}
      >
        {withdrawInProgress ? t('stakingBox2.processing') : t('stakingBox2.withdrawTokens')}
      </button>
      
      {/* Modal de notificación estilo StakingBox4 */}
      {showNotificationModal && (
        <div className="modal-overlay" onClick={closeNotificationModal}>
          <div className={`modal-container ${modalType}`} onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeNotificationModal}>&times;</span>
            <div className="modal-content">
              <h3>{modalType === "success" ? t('stakingBox2.success') : t('stakingBox2.info')}</h3>
              <p className={modalType === "error" ? "smaller-text" : ""}>{modalMessage}</p>
              <button className="modal-button" onClick={closeNotificationModal}>
                {t('stakingBox2.accept')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de información sobre el porcentaje del pool */}
      {showInfoModal && (
        <div className="info-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="info-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('stakingBox2.infoModal.title')}</h3>
            <p>
              {t('stakingBox2.infoModal.content')}
            </p>
            <button onClick={() => setShowInfoModal(false)}>{t('stakingBox2.infoModal.understood')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingBox2;
